<template>
  <div class="additional-info-tab">
    <div class="row mb-2" v-if="validationErrors.apiError">
      <div class="col">
        <el-alert type="error">
          {{ validationErrors.apiError }}
        </el-alert>
      </div>
    </div>

        <!-- Budget Breakdown Section -->
    <div class="row">
      <div class="col-12">
        <base-input 
          label="What is the client's budget breakdown? (optional)"
        >
          
          <div class="row">
            <div class="col-md-6">
              <base-input 
                label="F&B Budget"
                type="text"
                v-model="form.f_and_b_budget"
                placeholder="$0"
                @input="$delete(validationErrors, 'f_and_b_budget')"
              >
                  <template slot="error" v-if="validationErrors.f_and_b_budget">
                      <div class="invalid-feedback" style="display: block;">
                          {{ validationErrors.f_and_b_budget }}
                      </div>
                  </template>
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input 
                label="Accommodation Budget"
                type="text"
                v-model="form.accommodation_budget"
                placeholder="$0"
                @input="$delete(validationErrors, 'accommodation_budget')"
              >
                  <template slot="error" v-if="validationErrors.accommodation_budget">
                      <div class="invalid-feedback" style="display: block;">
                          {{ validationErrors.accommodation_budget }}
                      </div>
                  </template>
              </base-input>
            </div>
          </div>
          
          <div class="row mt-3">
            <div class="col-md-6">
              <base-input 
                label="DDP Budget (per head)"
                type="text"
                v-model="form.ddp_budget"
                placeholder="$0"
                @input="$delete(validationErrors, 'ddp_budget')"
              >
                  <template slot="error" v-if="validationErrors.ddp_budget">
                      <div class="invalid-feedback" style="display: block;">
                          {{ validationErrors.ddp_budget }}
                      </div>
                  </template>
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input 
                label="AV Budget"
                type="text"
                v-model="form.av_budget"
                placeholder="$0"
                @input="$delete(validationErrors, 'av_budget')"
              >
                  <template slot="error" v-if="validationErrors.av_budget">
                      <div class="invalid-feedback" style="display: block;">
                          {{ validationErrors.av_budget }}
                      </div>
                  </template>
              </base-input>
            </div>
          </div>
        </base-input>
      </div>
    </div>

    <!-- Previous Event Experience Section -->
    <div class="row">
      <div class="col-12">
        <base-input 
          label="Has the client hosted this event previously?"
        >
          <el-radio-group v-model="form.previous_occurrence">
            <el-radio :label="false">No</el-radio>
            <el-radio :label="true">Yes</el-radio>
          </el-radio-group>
        </base-input>

        <!-- Show venue input if Yes is selected -->
        <div v-if="form.previous_occurrence" class="mt-3">
          <base-input 
            label="Where did the client host their last event?"
            v-model="form.previous_venue"
            placeholder="Enter venue name"
          />
        </div>
      </div>
    </div>

    <!-- Venue Lock-in Timeline Section -->
    <div class="row">
      <div class="col-12">
        <base-input 
          label="When is the client wanting to lock in their venue?"
        >
          <el-select 
            v-model="form.booking_urgency" 
            placeholder="Immediately"
            :clearable="true"
          >
            <el-option
              v-for="option in venueLockTimelineOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </base-input>
      </div>
    </div>

    <!-- Additional Notes/Comments Section -->
    <div class="row">
        <div class="col-12">
            <div class="form-section">
                <base-input
                    label="Additional notes/comments"
                >
                    <textarea
                        class="form-control"
                        v-model="form.additional_comments"
                        :rows="4"
                    ></textarea>
                </base-input>
            </div>
        </div>
    </div>

    <!-- Save Button -->
    <div class="row mt-4">
      <div class="col-12 text-right">
        <base-button 
          type="primary" 
          @click="saveAdditionalInfo"
          :loading="loading"
        >
          Save & Finish
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Notification,
  Radio,
  RadioGroup,
  Alert
} from "element-ui";
import { mapState } from "vuex";
import LeadEventService from '@/services/lead-event.service';
import leadEventFormMixin from '@/helpers/leadEventFormMixin';

export default {
  name: 'EditEventStep5',
  mixins: [leadEventFormMixin],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Notification.name]: Notification,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
	[Alert.name]: Alert
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
    sessionId: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      validationErrors: {},
      fieldErrors: {},
      form: {
        step: 5,
        direct_update: true,
        f_and_b_budget: null,
        accommodation_budget: null,
        ddp_budget: null,
        av_budget: null,
        previous_occurrence: null,
        previous_venue: null,
        booking_urgency: null,
        additional_comments: null,
      },
      venueLockTimelineOptions: [],
    };
  },
  computed: {
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
  },
  methods: {
    async saveAdditionalInfo() {
      this.validationErrors = {};
      let isValid = true;

      // Validate numeric fields if they have values
      if (this.form.f_and_b_budget && isNaN(this.form.f_and_b_budget)) {
        this.$set(this.validationErrors, 'f_and_b_budget', 'F&B Budget must be a number.');
        isValid = false;
      }
      if (this.form.accommodation_budget && isNaN(this.form.accommodation_budget)) {
        this.$set(this.validationErrors, 'accommodation_budget', 'Accommodation Budget must be a number.');
        isValid = false;
      }
      if (this.form.ddp_budget && isNaN(this.form.ddp_budget)) {
        this.$set(this.validationErrors, 'ddp_budget', 'DDP Budget must be a number.');
        isValid = false;
      }
      if (this.form.av_budget && isNaN(this.form.av_budget)) {
        this.$set(this.validationErrors, 'av_budget', 'AV Budget must be a number.');
        isValid = false;
      }

      if (!isValid) {
        return;
      }

      this.$emit('update:loading', true);
      try {
        // Send form data directly to backend
        if (this.sessionId) {
          await LeadEventService.updateEventSession(this.leadEvent.id, {
            ...this.form,
            session_id: this.sessionId,
          });
        } else {
          await LeadEventService.updateCorporateLeadEvent(this.leadEvent.id, this.form);
        }

        // Emit success event to parent
        this.$emit('onSave', {
          message: 'Additional info saved successfully',
        });
        
        // Show success message
        Notification.success({
          title: 'Success',
          message: 'Additional info saved successfully!',
          type: 'success',
        });
        
      } catch (error) {
        console.error('Error saving additional info:', error);
        // Show error message
        const responseData = error?.response?.data || {};
        const message = responseData.error?.message
          || responseData.message
          || 'Failed to save additional info. Please check your inputs and try again.';

        if (responseData.errors && typeof responseData.errors === 'object') {
          Object.keys(responseData.errors).forEach((field) => {
            const firstError = Array.isArray(responseData.errors[field])
              ? responseData.errors[field][0]
              : responseData.errors[field];

            this.$set(this.validationErrors, field, firstError);
          });
        }

        this.$set(this.validationErrors, 'apiError', message);

        Notification.error({
          title: 'Error',
          message: message,
          type: 'error',
          duration: 5000,
        });
        this.$emit('update:loading', false);
      }
    },
    
    updateFormFromLeadEvent(leadEvent) {
      // Setting default options for venue lock timeline.
      this.venueLockTimelineOptions = leadEvent?.options?.booking_urgency || [];

      // Update form fields from leadEvent data, preserving existing values when new data is missing
      const safeUpdate = (currentValue, newValue) => {
        return ! [null, undefined, ''].includes(newValue) ? newValue : currentValue;
      };

      let dataToUse = leadEvent;
      if (this.sessionId) {
        const sessions = leadEvent.event_sessions || leadEvent.eventSessions || [];
        const session = sessions.find(s => s.id === this.sessionId);
        if (session) {
            console.log('found session for sessionId:', this.sessionId);
          dataToUse = {
            ...leadEvent, // Fallback to leadEvent for missing fields
            ...session
          };
        }
      }

      // Handle has happened before boolean conversion
      let hasHappenedBefore = this.safeBooleanUpdate(dataToUse.has_happened_before) || false;

      this.form.f_and_b_budget = safeUpdate(this.form.f_and_b_budget, dataToUse.f_and_b_budget);
      this.form.accommodation_budget = safeUpdate(this.form.accommodation_budget, dataToUse.accommodation_budget);
      this.form.ddp_budget = safeUpdate(this.form.ddp_budget, dataToUse.per_head_ddp_budget);
      this.form.av_budget = safeUpdate(this.form.av_budget, dataToUse.av_budget);
      this.form.previous_occurrence = safeUpdate(this.form.previous_occurrence, hasHappenedBefore);
      this.form.previous_venue = safeUpdate(this.form.previous_venue, dataToUse.last_hosting_venue);
      this.form.booking_urgency = safeUpdate(this.form.booking_urgency, dataToUse.venue_booking_urgency);
      this.form.additional_comments = safeUpdate(this.form.additional_comments, dataToUse.notes);

      console.log('Additional info form updated with data:', this.form);
    }
  },
  mounted() {
    // Initialize form with leadEvent data when component is mounted
    if (this.leadEvent) {
      this.updateFormFromLeadEvent(this.leadEvent);
    }
  },
  watch: {
    leadEvent: {
      handler(newLeadEvent) {
        if (newLeadEvent) {
          this.updateFormFromLeadEvent(newLeadEvent);
        }
      },
      deep: true,
      immediate: true
    },
    sessionId: {
      handler(newSessionId) {
        this.updateFormFromLeadEvent(this.leadEvent);
      },
      immediate: false
    }
  }
};
</script>

<style lang="scss" scoped>
.additional-info-tab {
  .el-radio {
    margin-right: 20px;
  }
}
</style>
